import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import {
  fetchContactsPage,
  searchAllContactPages,
  sanitizeSearchTerm,
} from "../utils/contactsFetcher";
import { useDebouncedCallback } from "./useDebounce";

const INITIAL_SEARCH_FIELDS = {
  id: "",
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
};

// SECURITY: Debounce delay for auto-search (in milliseconds)
const SEARCH_DEBOUNCE_DELAY = 2000;

// Merge contacts into a single list, de-duping by `id` (incoming overwrites existing on id match).
// NOTE: Contacts without an `id` are appended and cannot be de-duped safely.
const mergeUniqueContactsById = (base, incoming) => {
  const baseArr = Array.isArray(base) ? base : [];
  const incomingArr = Array.isArray(incoming) ? incoming : [];

  const byId = new Map();
  const orderedIds = [];
  const noId = [];

  baseArr.forEach((contact) => {
    const id = contact?.id;
    if (id === undefined || id === null) {
      noId.push(contact);
      return;
    }
    const key = String(id);
    if (!byId.has(key)) orderedIds.push(key);
    byId.set(key, contact);
  });

  incomingArr.forEach((contact) => {
    const id = contact?.id;
    if (id === undefined || id === null) {
      noId.push(contact);
      return;
    }
    const key = String(id);
    if (!byId.has(key)) orderedIds.push(key);
    byId.set(key, contact);
  });

  return [...orderedIds.map((key) => byId.get(key)), ...noId];
};

/**
 * Custom hook for contact search and loading functionality.
 *
 * @param {string} customer - Customer identifier for API calls
 * @param {Object} options - Configuration options
 * @param {Function} options.processContacts - Optional function to transform contacts after fetching
 * @param {Function} options.onContactsUpdated - Optional callback when contacts are updated
 * @param {number} options.pageSize - Page size for fetching (default: 50)
 * @returns {Object} Contact search state and handlers
 */
const useContactSearch = (customer, options = {}) => {
  const { processContacts = null, onContactsUpdated = null, pageSize = 50 } = options;

  // Core contact state
  const [contacts, setContacts] = useState([]);
  const [originalContacts, setOriginalContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [noContacts, setNoContacts] = useState(false);

  // Search state
  const [searchFields, setSearchFields] = useState(INITIAL_SEARCH_FIELDS);
  const [isServerSearch, setIsServerSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [nextPageToken, setNextPageToken] = useState(null);
  const [originalNextPageToken, setOriginalNextPageToken] = useState(null);

  // Refs to track current state values without causing dependency changes
  const contactsRef = useRef(contacts);
  const nextPageTokenRef = useRef(nextPageToken);
  const isLoadingMoreRef = useRef(false);
  contactsRef.current = contacts;
  nextPageTokenRef.current = nextPageToken;

  // SECURITY: Default contact processor adds name field
  const defaultProcessContacts = useCallback((contactList) => {
    return contactList.map((contact) => ({
      ...contact,
      name: contact.firstname || "",
    }));
  }, []);

  // Use provided processor or default
  const processContactList = useCallback(
    (contactList) => {
      if (processContacts && typeof processContacts === "function") {
        return processContacts(contactList);
      }
      return defaultProcessContacts(contactList);
    },
    [processContacts, defaultProcessContacts]
  );

  // Ref to hold the latest server search function for debounce callback
  const handleServerSearchRef = useRef(null);

  // Ref to hold the latest clear search function
  const handleClearSearchRef = useRef(null);

  const {
    debouncedCallback: triggerDebouncedSearch,
    isPending: isSearchPending,
    cancel: cancelDebouncedSearch,
    reset: resetDebouncedSearch,
  } = useDebouncedCallback(() => {
    if (typeof handleServerSearchRef.current === "function") {
      // SECURITY: Wrap async call to catch Promise rejections and prevent unhandled errors
      Promise.resolve(handleServerSearchRef.current()).catch((error) => {
        console.error("Debounced search failed:", error);
      });
    }
  }, SEARCH_DEBOUNCE_DELAY);

  // Handle search field changes with input sanitization and debounced auto-search
  const handleSearchFieldChange = useCallback(
    (field, value) => {
      const sanitizedValue = sanitizeSearchTerm(value);

      setSearchFields((prev) => {
        const newFields = {
          ...prev,
          [field]: sanitizedValue,
        };

        // Check if all fields are empty or only whitespace
        const allFieldsEmpty = Object.values(newFields).every((val) =>
          typeof val !== "string" ? !val : val.trim() === ""
        );

        if (allFieldsEmpty) {
          // Cancel debounce and restore original contacts immediately
          cancelDebouncedSearch();
          setTimeout(() => {
            if (handleClearSearchRef.current) {
              handleClearSearchRef.current();
            }
          }, 0);
        } else {
          // Trigger debounced search
          triggerDebouncedSearch();
        }

        return newFields;
      });
    },
    [cancelDebouncedSearch, triggerDebouncedSearch]
  );

  // Handle search field focus to reset debounce timer when user clicks into a field
  const handleSearchFieldFocus = useCallback(() => {
    // Reset the debounce timer if there's a pending search
    if (isSearchPending) {
      resetDebouncedSearch();
    }
  }, [isSearchPending, resetDebouncedSearch]);

  // Fetch contacts with pagination support
  const getContacts = useCallback(
    async (loadMore = false) => {
      if (!customer) return;

      // Prevent concurrent loadMore calls to avoid duplicate contacts
      if (loadMore && isLoadingMoreRef.current) return;

      try {
        if (loadMore) {
          isLoadingMoreRef.current = true;
        } else {
          setContactsLoaded(false);
        }

        // Fetch one page at a time, use refs to read current values without adding them as dependencies
        const result = await fetchContactsPage(
          customer,
          pageSize,
          loadMore ? nextPageTokenRef.current : null
        );

        if (result?.contacts?.length) {
          const processedContacts = processContactList(result.contacts);

          // Append or replace contacts based on loadMore flag
          const finalContacts = loadMore
            ? mergeUniqueContactsById(contactsRef.current, processedContacts)
            : processedContacts;
          setContacts(finalContacts);
          setOriginalContacts(finalContacts);
          setNextPageToken(result.nextToken);
          setOriginalNextPageToken(result.nextToken);
          setNoContacts(false);

          if (onContactsUpdated && typeof onContactsUpdated === "function") {
            try {
              onContactsUpdated(finalContacts);
            } catch (error) {
              console.error("Error in onContactsUpdated callback:", error);
            }
          }
        } else if (!loadMore) {
          setNoContacts(true);
          setContacts([]);
          setOriginalContacts([]);
        }

        setContactsLoaded(true);
      } catch (error) {
        console.error("Error fetching contacts:", error);
        setContactsLoaded(true);
        throw error;
      } finally {
        isLoadingMoreRef.current = false;
      }
    },
    [customer, pageSize, processContactList, onContactsUpdated]
  );

  // Build search parameters only with non-empty fields
  const buildSearchParams = useCallback(() => {
    const searchParams = {};
    if (searchFields.id) searchParams.id = searchFields.id;
    if (searchFields.firstname) searchParams.first_name = searchFields.firstname;
    if (searchFields.lastname) searchParams.last_name = searchFields.lastname;
    if (searchFields.email) searchParams.email = searchFields.email;
    if (searchFields.phone) searchParams.phone = searchFields.phone;
    return searchParams;
  }, [searchFields]);

  // Perform server-side search with validated parameters
  const handleServerSearch = useCallback(async () => {
    if (!customer) return;

    // Cancel any pending debounce when search is manually triggered
    cancelDebouncedSearch();

    try {
      setIsSearching(true);
      setIsServerSearch(true);

      const searchParams = buildSearchParams();

      // Fetch all search result pages with progress updates
      const searchResults = await searchAllContactPages(
        customer,
        searchParams,
        (pageContacts, allContactsSoFar) => {
          const processedContacts = processContactList(allContactsSoFar);
          setContacts(processedContacts);

          // Ensure server-side search results persist in "main" results after clearing search.
          setOriginalContacts((prev) => mergeUniqueContactsById(prev, processedContacts));

          if (onContactsUpdated && typeof onContactsUpdated === "function") {
            try {
              onContactsUpdated(processedContacts);
            } catch (error) {
              console.error("Error in onContactsUpdated callback:", error);
            }
          }
        }
      );

      if (searchResults.length === 0) {
        setNoContacts(true);
        setContacts([]);
      } else {
        // Final merge to cover cases where progress callback isn't invoked as expected.
        const processedContacts = processContactList(searchResults);
        setOriginalContacts((prev) => mergeUniqueContactsById(prev, processedContacts));
        setNoContacts(false);
      }

      setIsSearching(false);
    } catch (error) {
      console.error("Error performing server search:", error);
      setIsSearching(false);
      throw error;
    }
  }, [customer, buildSearchParams, processContactList, onContactsUpdated, cancelDebouncedSearch]);

  // Keep ref updated with latest search function for the debounced callback
  useEffect(() => {
    handleServerSearchRef.current = handleServerSearch;
  }, [handleServerSearch]);

  // Clear search and restore memoized data without reloading
  const handleClearSearch = useCallback(() => {
    // Cancel any pending debounce when search is cleared
    cancelDebouncedSearch();

    setSearchFields(INITIAL_SEARCH_FIELDS);
    setIsServerSearch(false);
    setContacts(originalContacts);
    setNextPageToken(originalNextPageToken);

    if (originalContacts.length === 0) {
      setNoContacts(true);
    } else {
      setNoContacts(false);
    }

    if (onContactsUpdated && typeof onContactsUpdated === "function") {
      try {
        onContactsUpdated(originalContacts);
      } catch (error) {
        console.error("Error in onContactsUpdated callback:", error);
      }
    }
  }, [originalContacts, originalNextPageToken, onContactsUpdated, cancelDebouncedSearch]);

  // Keep ref updated with latest clear function for the debounced callback
  useEffect(() => {
    handleClearSearchRef.current = handleClearSearch;
  }, [handleClearSearch]);

  // Getter for loading state to prevent race conditions in pagination
  const isLoadingMore = useCallback(() => isLoadingMoreRef.current, []);

  // Check if we should load more data based on pagination
  const shouldLoadMore = useCallback(
    (currentPage, pageSize, isLastPage) => {
      return (
        isLastPage && nextPageToken && !isServerSearch && !isSearching && !isLoadingMoreRef.current
      );
    },
    [nextPageToken, isServerSearch, isSearching]
  );

  // Filter contacts in memory based on search fields (real-time filtering as you type)
  const filterContactsInMemory = useCallback(
    (contactList) => {
      if (
        !searchFields.id &&
        !searchFields.firstname &&
        !searchFields.lastname &&
        !searchFields.email &&
        !searchFields.phone
      ) {
        return contactList;
      }

      return contactList.filter((contact) => {
        const matchId =
          !searchFields.id ||
          (contact.id && contact.id.toLowerCase().includes(searchFields.id.toLowerCase()));
        const matchFirstname =
          !searchFields.firstname ||
          (contact.firstname &&
            contact.firstname.toLowerCase().includes(searchFields.firstname.toLowerCase()));
        const matchLastname =
          !searchFields.lastname ||
          (contact.lastname &&
            contact.lastname.toLowerCase().includes(searchFields.lastname.toLowerCase()));
        const matchEmail =
          !searchFields.email ||
          (contact.email && contact.email.toLowerCase().includes(searchFields.email.toLowerCase()));
        const matchPhone =
          !searchFields.phone ||
          (contact.phone && contact.phone.toLowerCase().includes(searchFields.phone.toLowerCase()));

        return matchId && matchFirstname && matchLastname && matchEmail && matchPhone;
      });
    },
    [searchFields]
  );

  // Memoize filtered contacts to prevent unnecessary recalculations
  const filteredContacts = useMemo(
    () => filterContactsInMemory(contacts),
    [filterContactsInMemory, contacts]
  );

  return {
    // State
    contacts,
    filteredContacts,
    contactsLoaded,
    noContacts,
    isSearching,
    isServerSearch,
    searchFields,
    nextPageToken,
    isSearchPending,

    // Handlers
    handleSearchFieldChange,
    handleSearchFieldFocus,
    handleServerSearch,
    handleClearSearch,
    getContacts,
    shouldLoadMore,
    isLoadingMore,

    // Setters for edge cases requiring direct state manipulation
    setContacts,
    setContactsLoaded,
  };
};

export default useContactSearch;
